import { AgGridTable } from 'components/AgGrid/AgGridTable';
import { HEADER_HEIGHT } from 'components/Layout/Master';
import { TableTopPanel } from 'components/TableTopPanel';
import type { ProjectItemData } from 'gql/graphql';
import { useAccess } from 'hooks/useAccess';
import { useSendGpnAnalyticsEvent } from 'hooks/useSendGpnAnalyticsEvent';
import { Button, Grid, GridItem, HeaderSearchBar, IconAdd, IconClose } from 'uikit';

import type { AgGridReact } from 'ag-grid-react';
import type { CSSProperties } from 'react';
import { useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { useProjectListColumns } from './hooks/useProjectListColumns';
import { useProjectListData } from './hooks/useProjectListData';
import { defaultRowStyle, defaultSet, projectListContext, TITLE_HEIGHT } from './projectListHelpers';

import './ProjectList.css';

export const ProjectList = (): JSX.Element => {
    const { project: projectRules } = useAccess();

    const tableRef = useRef<AgGridReact | null>(null);
    const tableApi = useMemo(() => (tableRef && 'current' in tableRef ? tableRef.current : undefined), [tableRef]);

    const { sendGpnAnalyticsEvent } = useSendGpnAnalyticsEvent();

    const navigate = useNavigate();

    const handleOnCreate = useCallback(() => {
        sendGpnAnalyticsEvent({ componentId: 'rim_create project button_pressed' });
        navigate('/projects/create');
    }, [navigate, sendGpnAnalyticsEvent]);

    const handleRowClick = useCallback(
        ({ data }: { data: ProjectItemData | undefined }) => {
            const { uniqueId, methodology } = data || {};

            if (methodology && uniqueId) {
                const loweredMethodology = methodology.toLocaleLowerCase();

                navigate(`/projects/${loweredMethodology}/${uniqueId}`);
            }
        },
        [navigate],
    );

    const {
        handleSearch,
        searchString,
        handleChangeSearchString,
        handleClearSearch,
        dataSource,
        fetchedItems,
        totalCount,
        tableKey,
    } = useProjectListData();

    const { columns, fullSetSettingsColumns, isColumnVisible, tableSettingsConfirm } = useProjectListColumns();

    return (
        <div id="projectList" style={{ '--others-height': `${HEADER_HEIGHT + TITLE_HEIGHT}px` } as CSSProperties}>
            <div style={{ height: TITLE_HEIGHT, padding: '24px 24px 0 24px', boxSizing: 'border-box' }}>
                <h2 className="project-list-title">Реестр проектов</h2>
                <Grid cols="2" colGap="xl" rowGap="l" style={{ marginBottom: 16 }}>
                    <GridItem style={{ display: 'flex', alignItems: 'center' }}>
                        <HeaderSearchBar
                            placeholder="Найти проект"
                            label="поиск"
                            value={searchString}
                            onChange={handleChangeSearchString}
                            onSearch={handleSearch}
                        />
                        {searchString && (
                            <Button
                                label="Очистить"
                                view="ghost"
                                iconLeft={IconClose}
                                form="round"
                                size="xs"
                                onlyIcon
                                onClick={handleClearSearch}
                                style={{ position: 'relative', left: -30, zIndex: 10 }}
                            />
                        )}
                    </GridItem>
                    <GridItem style={{ display: 'flex', justifyContent: 'right' }}>
                        <Button
                            disabled={!projectRules.canCreate}
                            label="Создать проект"
                            iconLeft={IconAdd}
                            onClick={handleOnCreate}
                        />
                    </GridItem>
                </Grid>
                <TableTopPanel
                    totalItems={totalCount}
                    fetchedItems={fetchedItems}
                    optionalColumns={fullSetSettingsColumns}
                    isColumnVisible={isColumnVisible}
                    handleTableSettingsConfirm={tableSettingsConfirm}
                    showPinColumn
                    tableApi={tableApi}
                />
            </div>

            <div className="Table">
                <AgGridTable
                    ref={tableRef}
                    datasource={dataSource}
                    columnDefs={columns}
                    maxConcurrentDatasourceRequests={1}
                    maxBlocksInCache={50}
                    cacheBlockSize={50}
                    rowModelType="infinite"
                    defaultColDef={defaultSet}
                    rowStyle={defaultRowStyle}
                    onRowClicked={handleRowClick}
                    context={projectListContext}
                    key={tableKey}
                    headerHeight={40}
                />
            </div>
        </div>
    );
};

ProjectList.displayName = 'ProjectList';
